import { useEffect, useState } from "react";
import logoBlackText from "../assets/images/logo-blck-text.png";

const STEPS = [
  { label: "Verifying your account", doneAt: 25 },
  { label: "Loading clinic data", doneAt: 50 },
  { label: "Checking subscription status", doneAt: 75 },
  { label: "Setting up your workspace", doneAt: 100 },
];

function Shield() {
  return (
    <svg className="cq-shield" viewBox="0 0 48 54" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="cq-shield-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#1fe0a8" />
          <stop offset="1" stopColor="#0aa878" />
        </linearGradient>
      </defs>
      <path
        d="M24 1 C29 4.5 38 6.5 46 6.5 V28 C46 41 36 49.5 24 53 C12 49.5 2 41 2 28 V6.5 C10 6.5 19 4.5 24 1 Z"
        fill="url(#cq-shield-g)"
      />
      <path
        d="M24 1 C19 4.5 10 6.5 2 6.5 V28 C2 41 12 49.5 24 53 Z"
        fill="#fff"
        opacity="0.14"
      />
      <polyline
        points="14,28 22,36 35,20"
        fill="none"
        stroke="#fff"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StepIcon({ status }) {
  if (status === "done") {
    return (
      <span className="cq-dot cq-dot--done">
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <polyline
            points="6,12.5 10.5,17 18,8"
            fill="none"
            stroke="#fff"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  return <span className={`cq-dot cq-dot--${status}`} />;
}

export default function CliniQueueLoading({
  progress = 0,
  animate = false,
  subscriptionMode = null,
  onRenew,
}) {
  const [value, setValue] = useState(progress);

  useEffect(() => {
    setValue(progress);
  }, [progress]);

  useEffect(() => {
    if (!animate) return undefined;
    const startedAt = Date.now();
    setValue(0);
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setValue(Math.min(100, Math.round((elapsed / 8000) * 100)));
    }, 50);
    return () => clearInterval(id);
  }, [animate]);

  const activeIndex = STEPS.findIndex((step) => value < step.doneAt);
  const isExpired = subscriptionMode === "EXPIRED";

  return (
    <div className="cq-page">
      <style>{css}</style>

      <div className="cq-deco cq-deco--blob-left" />
      <div className="cq-deco cq-deco--circle-big" />
      <div className="cq-deco cq-deco--circle-small" />

      <div className="cq-deco cq-tagline-top">
        Smarter Clinics.
        <br />
        Happier Patients.
      </div>

      <div className="cq-deco cq-script cq-script--left">
        <div className="cq-script-text">
          Better
          <br />
          Care
          <br />
          Every Day
        </div>
        <svg className="cq-swoosh" viewBox="0 0 120 50" aria-hidden="true">
          <path d="M3 46 C 40 24, 88 10, 116 6" fill="none" stroke="#8db3f3" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>

      <div className="cq-deco cq-script cq-script--right">
        <div className="cq-script-text">
          For a
          <br />
          Smoother
          <br />
          Clinic Flow
        </div>
        <svg className="cq-swoosh" viewBox="0 0 120 50" aria-hidden="true">
          <path d="M3 46 C 40 30, 84 16, 116 6" fill="none" stroke="#7fa8f0" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>

      <div className="cq-deco cq-powering">
        <svg viewBox="0 0 50 46" width="50" height="46" aria-hidden="true">
          <polyline
            points="1,24 14,24 19,6 27,40 32,24 49,24"
            fill="none"
            stroke="#7fa2e8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>
          Powering Clinics
          <br />
          Across India
        </span>
      </div>

      <main className="cq-card">
        <div className="cq-inner">
          <div className="cq-logo">
            <img
              src={logoBlackText}
              alt="CliniQueue"
              className="cq-brand-image"
            />
          </div>

          <h1 className="cq-title">
            {isExpired ? "Subscription expired" : "Preparing your clinic..."}
          </h1>
          <p className="cq-subtitle">
            {isExpired
              ? "Your clinic subscription is no longer active. Renew to continue using the dashboard."
              : "Please wait while we load your clinic data and set up your workspace."}
          </p>

          {!isExpired && (
            <>
              <ol className="cq-steps">
                {STEPS.map((step, index) => {
                  const status =
                    activeIndex === -1 || index < activeIndex
                      ? "done"
                      : index === activeIndex
                        ? "active"
                        : "pending";

                  return (
                    <li key={step.label} className={`cq-step cq-step--${status}`}>
                      <StepIcon status={status} />
                      <span className="cq-step-label">{step.label}</span>
                    </li>
                  );
                })}
              </ol>

              <div className="cq-progress-row">
                <div
                  className="cq-track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={value}
                  aria-label="Clinic setup progress"
                >
                  <div className="cq-fill" style={{ width: `${value}%` }} />
                </div>
                <span className="cq-percent">{value}%</span>
              </div>
            </>
          )}

          {isExpired ? (
            <div className="cq-expired-actions">
              <button type="button" className="cq-renew-btn" onClick={onRenew}>
                Renew subscription
              </button>
            </div>
          ) : (
            <div className="cq-secure">
              <Shield />
              <div>
                <div className="cq-secure-title">Your data is safe and secure</div>
                <div className="cq-secure-sub">
                  Please keep this window open while we prepare your clinic.
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&display=swap');

html, body, #root {
  margin: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.cq-page {
  --navy: #0d2358;
  --muted: #66779c;
  --blue: #1f63f2;
  position: relative;
  width: 100%;
  max-width: 100vw;
  min-height: 100dvh;
  height: 100dvh;
  max-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: var(--navy);
  background: radial-gradient(900px 600px at 15% 8%, #f8fbff 0%, rgba(248,251,255,0) 70%),
    linear-gradient(180deg, #eff5fe 0%, #e7f0fc 100%);
  box-sizing: border-box;
}

.cq-page *, .cq-page *::before, .cq-page *::after {
  box-sizing: border-box;
}

.cq-deco {
  position: absolute;
  pointer-events: none;
  user-select: none;
}

.cq-deco--blob-left {
  left: 0;
  bottom: 0;
  width: 340px;
  height: 505px;
  background: #e6effc;
  opacity: 0.75;
  clip-path: path('M0 0 C 90 5 150 65 200 140 C 230 185 270 230 340 270 L 340 505 L 0 505 Z');
}

.cq-deco--circle-big {
  right: -170px;
  top: 204px;
  width: 340px;
  height: 340px;
  border-radius: 50%;
  background: #e8f0fc;
}

.cq-deco--circle-small {
  right: 100px;
  top: 156px;
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background: #e3ecfb;
}

.cq-tagline-top {
  top: 40px;
  right: 47px;
  font-size: 16px;
  line-height: 22px;
  color: #5f7fd0;
  text-align: left;
}

.cq-script {
  color: #8db3f3;
}

.cq-script-text {
  font-family: 'Dancing Script', 'Segoe Script', cursive;
  font-weight: 500;
  font-size: clamp(28px, 2vw, 42px);
  line-height: 1.02;
  transform: rotate(-12deg);
  transform-origin: left bottom;
  white-space: nowrap;
}

.cq-swoosh {
  display: block;
  width: 120px;
  height: 50px;
}

.cq-script--left {
  left: 50px;
  top: 108px;
}

.cq-script--left .cq-swoosh {
  margin: 4px 0 0 34px;
}

.cq-script--right {
  right: 34px;
  bottom: 100px;
  color: #7fa8f0;
}

.cq-script--right .cq-script-text {
  transform: rotate(-20deg);
  font-size: clamp(26px, 1.9vw, 40px);
}

.cq-script--right .cq-swoosh {
  margin: 10px 0 0 50px;
}

.cq-powering {
  left: 40px;
  bottom: 60px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  line-height: 20px;
  color: #7a95d6;
}

.cq-card {
  position: relative;
  z-index: 1;
  width: min(100%, 620px);
  max-height: min(82dvh, 650px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,.88);
  border-radius: 24px;
  padding: clamp(18px, 1.8vw, 28px) clamp(14px, 1.6vw, 22px);
  box-shadow: 0 18px 55px rgba(66,110,190,.10), 0 2px 10px rgba(66,110,190,.05);
  overflow: hidden;
}

.cq-inner {
  max-width: 560px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
}

.cq-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: clamp(12px, 1.8vh, 18px);
}

.cq-brand-image {
  display: block;
  max-width: min(50vw, 200px);
  width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 10px rgba(19, 41, 73, 0.08));
}

.cq-title {
  margin: 0;
  text-align: center;
  font-family: 'Plus Jakarta Sans', 'Manrope', 'Inter', sans-serif;
  font-size: clamp(22px, 1.8vw, 32px);
  line-height: 1.18;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: var(--navy);
}

.cq-subtitle {
  margin: 8px 0 0;
  text-align: center;
  font-family: 'Manrope', 'Inter', sans-serif;
  font-size: clamp(13px, 1.05vw, 16px);
  line-height: 1.45;
  font-weight: 500;
  color: var(--muted);
}

.cq-steps {
  list-style: none;
  margin: clamp(16px, 2.2vh, 28px) 0 0;
  padding: 0;
  width: min(100%, 440px);
  margin-left: auto;
  margin-right: auto;
}

.cq-step {
  position: relative;
  display: flex;
  align-items: center;
  gap: clamp(12px, 1.5vw, 20px);
  min-height: 32px;
  margin-bottom: 12px;
  font-size: clamp(13px, 1vw, 16px);
  font-weight: 400;
  white-space: normal;
  padding-left: 8px;
}

.cq-step-label {
  display: block;
  min-width: 0;
  overflow-wrap: anywhere;
}

.cq-step:last-child {
  margin-bottom: 0;
}

.cq-step:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 24px;
  top: 32px;
  width: 2px;
  height: 12px;
  background: #e0e7f4;
}

.cq-step--done .cq-step-label {
  color: var(--navy);
}

.cq-step--active .cq-step-label {
  color: var(--blue);
  font-weight: 500;
}

.cq-step--pending .cq-step-label {
  color: #6a7a9d;
}

.cq-dot {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cq-dot--done {
  background: #2b6cf6;
  box-shadow: 0 3px 8px rgba(43,108,246,.28);
}

.cq-dot--active {
  border: 3px solid #2b6cf6;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(43,108,246,.06);
}

.cq-dot--pending {
  border: 2px solid #cfd8e8;
  background: #fff;
}

.cq-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
}

.cq-track {
  flex: 1;
  height: 16px;
  border-radius: 999px;
  background: #dbe3f0;
  overflow: hidden;
}

.cq-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #1d6bf5 0%, #2f80ff 100%);
  transition: width .4s ease;
}

.cq-percent {
  width: 42px;
  flex: none;
  font-size: 18px;
  font-weight: 600;
  color: var(--blue);
}

.cq-expired-actions {
  margin-top: 22px;
  display: flex;
  justify-content: center;
}

.cq-renew-btn {
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 24px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cq-renew-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(37, 99, 235, 0.28);
}

.cq-secure {
  margin-top: 26px;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px 18px 16px 30px;
  border-radius: 22px;
  background: rgba(226,235,250,.5);
  flex-wrap: wrap;
  justify-content: center;
}

.cq-shield {
  width: 36px;
  height: 42px;
  flex: none;
}

.cq-secure-title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--navy);
}

.cq-secure-sub {
  margin-top: 2px;
  font-size: 13px;
  line-height: 18px;
  color: var(--muted);
}

@media (max-width: 1200px) {
  .cq-deco {
    display: none;
  }
}

@media (max-width: 900px) {
  .cq-tagline-top,
  .cq-script,
  .cq-powering {
    display: none;
  }

  .cq-page {
    min-height: 100dvh;
    height: 100dvh;
    padding: 12px;
  }

  .cq-card {
    border-radius: 24px;
    max-height: calc(100dvh - 24px);
  }
}

@media (max-width: 760px) {
  .cq-page {
    align-items: center;
    justify-content: center;
    padding: 10px;
  }

  .cq-card {
    width: min(100%, 480px);
    max-height: calc(100dvh - 20px);
    padding: 18px 14px;
  }

  .cq-logo {
    margin-bottom: 12px;
  }

  .cq-brand-image {
    max-width: min(48vw, 180px);
  }

  .cq-steps {
    padding-left: 8px;
    margin-top: 22px;
  }

  .cq-step {
    gap: 12px;
    margin-bottom: 10px;
  }

  .cq-progress-row {
    margin-top: 24px;
    gap: 10px;
  }

  .cq-percent {
    font-size: 16px;
    width: 38px;
  }

  .cq-secure {
    padding: 14px 12px;
    gap: 12px;
    margin-top: 22px;
  }

  .cq-shield {
    width: 34px;
    height: 38px;
  }

  .cq-secure-title {
    font-size: 14px;
    line-height: 20px;
  }

  .cq-secure-sub {
    font-size: 12px;
    line-height: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cq-fill {
    transition: none;
  }
}
`;
